import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * Sticky marketing header with translucent background and blur.
 * Collapses to hamburger on mobile.
 */
function MarketingHeader({ ctaLabel, loginLabel, showLogin }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const isDark = theme.palette.mode === 'dark';

  const orangeMain = theme.palette.brand.orange.main;
  const goldenMain = theme.palette.brand.golden.main;
  const skyBlueMain = theme.palette.brand.skyBlue.main;

  const gradientText = isDark
    ? `linear-gradient(135deg, ${orangeMain}, ${skyBlueMain})`
    : `linear-gradient(135deg, ${orangeMain}, ${goldenMain})`;

  const handleScrollToSignup = () => {
    const el = document.getElementById('signup');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setAnchorEl(null);
  };

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: 1100,
        background: isDark
          ? 'rgba(15, 31, 61, 0.85)'
          : 'rgba(255, 248, 245, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${
          isDark ? 'rgba(255,255,255,0.08)' : 'rgba(27,54,93,0.08)'
        }`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 } }}>
        {/* Logo + Wordmark */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="BookMyLibrary logo"
            sx={{ width: 32, height: 32, borderRadius: '8px' }}
          />
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: '1.1rem',
              background: gradientText,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.01em',
            }}
          >
            BookMyLibrary
          </Typography>
        </Box>

        {/* Desktop nav */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showLogin && (
              <Button
                component={Link}
                to="/login"
                variant="text"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  '&:hover': { background: 'transparent', color: orangeMain },
                }}
              >
                {loginLabel}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleScrollToSignup}
              sx={{
                background: `linear-gradient(135deg, ${orangeMain}, ${goldenMain})`,
                color: '#fff',
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                boxShadow: '0 4px 16px rgba(242,101,34,0.3)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orangeMain})`,
                  boxShadow: '0 6px 20px rgba(242,101,34,0.4)',
                },
              }}
            >
              {ctaLabel}
            </Button>
          </Box>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <>
            <IconButton
              aria-label="Open navigation menu"
              onClick={handleOpen}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {showLogin && (
                <MenuItem component={Link} to="/login" onClick={handleClose}>
                  {loginLabel}
                </MenuItem>
              )}
              <MenuItem onClick={handleScrollToSignup}>
                {ctaLabel}
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

MarketingHeader.propTypes = {
  ctaLabel: PropTypes.string,
  loginLabel: PropTypes.string,
  showLogin: PropTypes.bool,
};

MarketingHeader.defaultProps = {
  ctaLabel: 'List your library',
  loginLabel: 'Login',
  showLogin: true,
};

export default MarketingHeader;
